import { useState } from "react";
import { BarChart3, ChevronDown, Lightbulb } from "lucide-react";

const METRICS = [
  ["ats_compatibility", "ATS Compatibility"],
  ["recruiter_readiness", "Recruiter Readiness"],
  ["professional_structure", "Professional Structure"],
  ["achievement_strength", "Achievement Strength"],
  ["keyword_optimisation", "Keyword Optimisation"],
];

function scoreColor(v) {
  if (v >= 85) return "#059669"; // emerald-600
  if (v >= 70) return "#F39319"; // brand
  return "#dc2626"; // red-600
}

/** Career Quality Report — scored at generation time by the backend.
 *  Shown pre- and post-payment; a key differentiator of the platform. */
export default function CareerReport({ report, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!report) return null;
  const overall = report.overall_score ?? 0;

  return (
    <div
      data-testid="career-report"
      className="bg-card border border-border rounded-2xl overflow-hidden"
    >
      <button
        type="button"
        data-testid="career-report-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-muted/40 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
            <BarChart3 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="font-serif text-lg font-bold leading-tight">
              Career Quality Report
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              How your CV scores against UK recruiter standards
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p
              data-testid="career-report-overall"
              className="font-serif text-3xl font-bold leading-none"
              style={{ color: scoreColor(overall) }}
            >
              {overall}
              <span className="text-sm text-muted-foreground font-sans font-semibold">
                /100
              </span>
            </p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-0.5">
              Overall CV Score
            </p>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-border pt-4">
          {report.headline && (
            <p className="text-sm text-foreground/80 leading-relaxed italic">
              “{report.headline}”
            </p>
          )}

          <div className="mt-4 space-y-3">
            {METRICS.map(([key, label]) => {
              const v = report[key] ?? 0;
              return (
                <div key={key} data-testid={`career-report-${key}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-foreground/80">{label}</span>
                    <span className="font-bold" style={{ color: scoreColor(v) }}>
                      {v}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${v}%`, backgroundColor: scoreColor(v) }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {Array.isArray(report.recommendations) && report.recommendations.length > 0 && (
            <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-4">
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-brand">
                <Lightbulb className="h-3.5 w-3.5" /> How to improve
              </p>
              <ul className="mt-2.5 space-y-2">
                {report.recommendations.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[13px] text-foreground/85 leading-relaxed"
                  >
                    <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-brand shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
